import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ThumbsUp, Plus, Filter, Search, TrendingUp, Clock, MessageSquare, CheckCircle, XCircle, Loader } from 'lucide-react';
import './FeedbackPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'performance', label: 'Performance', icon: '⚡' },
  { value: 'content', label: 'Inhalt', icon: '📝' },
  { value: 'navigation', label: 'Navigation', icon: '🧭' },
  { value: 'accessibility', label: 'Barrierefreiheit', icon: '♿' },
  { value: 'mobile', label: 'Mobile', icon: '📱' }
];

const STATUS_CONFIG = {
  new: { label: 'Neu', color: '#3b82f6', icon: '🆕' },
  reviewing: { label: 'In Prüfung', color: '#f59e0b', icon: '👀' },
  planned: { label: 'Geplant', color: '#8b5cf6', icon: '📋' },
  in_progress: { label: 'In Arbeit', color: '#06b6d4', icon: '⚙️' },
  live: { label: 'Live', color: '#10b981', icon: '✅' },
  rejected: { label: 'Abgelehnt', color: '#ef4444', icon: '❌' },
  duplicate: { label: 'Duplikat', color: '#6b7280', icon: '🔗' }
};

export default function FeedbackPage() {
  const { user, token } = useAuth();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'content',
    impact: 'medium',
    frequency: 'sometimes',
    contact_allowed: false
  });

  useEffect(() => {
    fetchFeedback();
  }, [selectedCategory, selectedStatus, sortBy]);

  const fetchFeedback = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);
      params.append('sort', sortBy);

      const response = await axios.get(`${API}/feedback?${params.toString()}`);
      setFeedback(response.data);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/feedback`, formData, { headers });
      setShowForm(false);
      setFormData({
        title: '',
        description: '',
        category: 'content',
        impact: 'medium',
        frequency: 'sometimes',
        contact_allowed: false
      });
      fetchFeedback();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Fehler beim Einreichen. Bitte versuche es erneut.');
    }
  };

  const handleVote = async (feedbackId) => {
    if (!user) {
      alert('Bitte melde dich an, um abzustimmen.');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/feedback/${feedbackId}/vote`, {}, { headers });
      fetchFeedback();
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  const filteredFeedback = feedback.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="feedback-page">
      <div className="feedback-header">
        <div>
          <h1>💡 Feedback & Verbesserungsvorschläge</h1>
          <p>Hilf uns, PedalScot noch besser zu machen! Teile deine Ideen und stimme für die besten Vorschläge.</p>
        </div>
        <button className="submit-feedback-btn" onClick={() => setShowForm(!showForm)}>
          <Plus size={20} />
          {showForm ? 'Abbrechen' : 'Feedback einreichen'}
        </button>
      </div>

      {showForm && (
        <div className="feedback-form-container">
          <h2>Neues Feedback einreichen</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Titel *</label>
              <input
                type="text"
                maxLength={100}
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Kurze Beschreibung deines Vorschlags"
                required
              />
            </div>

            <div className="form-group">
              <label>Beschreibung * (Problem → Vorschlag → Nutzen)</label>
              <textarea
                rows={6}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Was ist das Problem? Was schlägst du vor? Welchen Nutzen hätte es?"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Kategorie *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  required
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Auswirkung</label>
                <select
                  value={formData.impact}
                  onChange={(e) => setFormData({...formData, impact: e.target.value})}
                >
                  <option value="small">Klein</option>
                  <option value="medium">Mittel</option>
                  <option value="large">Groß</option>
                </select>
              </div>

              <div className="form-group">
                <label>Häufigkeit</label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                >
                  <option value="rarely">Selten</option>
                  <option value="sometimes">Oft</option>
                  <option value="always">Jedes Mal</option>
                </select>
              </div>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.contact_allowed}
                  onChange={(e) => setFormData({...formData, contact_allowed: e.target.checked})}
                />
                Ihr dürft mich für Rückfragen kontaktieren
              </label>
            </div>

            <button type="submit" className="submit-btn">Feedback einreichen</button>
          </form>
        </div>
      )}

      <div className="feedback-controls">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filters">
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
            <option value="all">Alle Kategorien</option>
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.icon} {cat.label}</option>
            ))}
          </select>

          <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
            <option value="all">Alle Status</option>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.icon} {config.label}</option>
            ))}
          </select>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="newest"><Clock size={16} /> Neueste</option>
            <option value="votes"><TrendingUp size={16} /> Meist Votes</option>
            <option value="hot">🔥 Hot</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <Loader className="spinner" size={40} />
          <p>Lade Feedback...</p>
        </div>
      ) : (
        <div className="feedback-list">
          {filteredFeedback.length === 0 ? (
            <div className="no-feedback">
              <p>Keine Feedback-Einträge gefunden.</p>
              <button onClick={() => setShowForm(true)}>Sei der Erste!</button>
            </div>
          ) : (
            filteredFeedback.map((item) => (
              <div key={item.id} className="feedback-item">
                <div className="vote-section">
                  <button
                    className={`vote-btn ${user && item.voter_ids.includes(user.id) ? 'voted' : ''}`}
                    onClick={() => handleVote(item.id)}
                    disabled={!user}
                  >
                    <ThumbsUp size={20} />
                  </button>
                  <span className="vote-count">{item.votes}</span>
                </div>

                <div className="feedback-content">
                  <div className="feedback-header-row">
                    <h3>{item.title}</h3>
                    <span
                      className="status-badge"
                      style={{ background: STATUS_CONFIG[item.status]?.color }}
                    >
                      {STATUS_CONFIG[item.status]?.icon} {STATUS_CONFIG[item.status]?.label}
                    </span>
                  </div>

                  <p className="feedback-description">{item.description}</p>

                  <div className="feedback-meta">
                    <span className="category-tag">
                      {CATEGORIES.find(c => c.value === item.category)?.icon}{' '}
                      {CATEGORIES.find(c => c.value === item.category)?.label}
                    </span>
                    <span className="author">von {item.author_name}</span>
                    <span className="date">{new Date(item.created_at).toLocaleDateString('de-DE')}</span>
                  </div>

                  {item.admin_notes && (
                    <div className="admin-notes">
                      <strong>Admin-Notiz:</strong> {item.admin_notes}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
